import { DevTool } from '@hookform/devtools';
import { yupResolver } from '@hookform/resolvers/yup';
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	FormGroup,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
	useCreatePaymentMutation,
	useUpdatePaymentAfterCreationMutation,
} from '../../../api/PaymentsApi';
import {
	MULTI_CURRENCY_FEATURE_ENABLE,
	UI_MULTI_CURRENCY_FEATURE_ENABLE,
	useAuthContext,
} from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import { usePolicyContext } from '../../../context/policy';
import currencyCodes from '../../../data/currencyCodes';
import { CurrencyCode } from '../../../types/currencyCode';
import { FastForm } from '../../fastForm';
import {
	FORM_NAME,
	PAYMENT_SPECIAL_FIELDS,
} from '../../fastForm/data/constants';
import {
	CurrencyField,
	PolicyholderLookupField,
	PolicyLookupField,
	SelectField,
} from '../../fastForm/fields';
import {
	getDefaultPolicyValues,
	getPaymentForm,
} from '../../fastForm/formBuilders';
import { PaymentFormValues } from '../../fastForm/formBuilders/getPaymentForm';
import paymentUpdateBuilder from '../../fastForm/updateBuilders/paymentUpdateBuilder';
import { getValidationSchemaWithoutResolver } from '../../fastForm/utils/getValidationSchema';
import ActiveLastBreadcrumb from '../components/Breadcrumbs';
import PaymentStateChip from '../components/PaymentStateChip';
import {
	CREATE_PAYMENT_PAGE__CANCEL,
	CREATE_PAYMENT_PAGE__READY_TO_POST_LABEL,
	CREATE_PAYMENT_PAGE__SUBMIT,
	CREATE_PAYMENT_PAGE__TITLE,
} from '../data/constants';
import { paymentSchema } from '../lib/paymentSchema';

const CreatePaymentPage = () => {
	const authData = useAuthContext();
	const {
		policyholderId: policyholderLocatorFromParams,
		policyId: policyLocatorFromParams,
	} = useParams();
	const policyData = usePolicyContext();
	const config = useConfigContext();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [readyToPost, setReadyToPost] = React.useState(false);
	const multiCurrencyEnabled =
		authData?.auth?.configProperties?.[MULTI_CURRENCY_FEATURE_ENABLE] &&
		authData?.auth?.configProperties?.[UI_MULTI_CURRENCY_FEATURE_ENABLE];
	const timezone = authData.auth?.timezone;
	const defaultCurrency = config?.tenantConfiguration?.currencyCode || 'USD';
	const authToken = authData.auth?.authorizationToken;
	const form = getPaymentForm({
		fields: config.paymentConfiguration.paymentConfiguration.fields,
		fieldValues: {},
		mediaByLocator: {},
		timezone,
		multiCurrencyEnabled,
	});

	const defaultValues = getDefaultPolicyValues(form, authData);

	const methods = useForm<PaymentFormValues>({
		defaultValues: {
			payment: {
				...(defaultValues.payment as object),
				policyholderLocator: '',
				policyLocator: '',
				amountCurrency: defaultCurrency,
				amount: '',
			},
		},
		mode: 'onBlur',
		reValidateMode: 'onChange',
		shouldFocusError: true,
		resolver:
			form &&
			yupResolver(
				getValidationSchemaWithoutResolver(form, !readyToPost).concat(
					paymentSchema(readyToPost),
				),
			),
	});

	const { handleSubmit, formState, control, watch, setValue } = methods;

	useEffect(() => {
		if (policyholderLocatorFromParams) {
			setValue('payment.policyholderLocator', policyholderLocatorFromParams, {
				shouldDirty: true,
			});
		}

		if (policyLocatorFromParams) {
			setValue(
				'payment.policyholderLocator',
				policyData?.policy?.policyholderLocator,
				{ shouldDirty: true },
			);
			setValue('payment.policyLocator', policyLocatorFromParams, {
				shouldDirty: true,
			});
		}
	}, [
		policyholderLocatorFromParams,
		policyLocatorFromParams,
		policyData,
		setValue,
	]);

	const currency = watch(
		`${FORM_NAME.payment as 'payment'}.${
			PAYMENT_SPECIAL_FIELDS.amountCurrency
		}`,
	) as CurrencyCode;

	const dirtyFields = formState.dirtyFields;

	const updatePayment = useUpdatePaymentAfterCreationMutation({
		authToken,
		queryClient,
	});

	const create = useCreatePaymentMutation({
		readyToPost,
		authToken,
		updatePayment,
		queryClient,
	});

	const onSubmit: SubmitHandler<PaymentFormValues> = async (formValues) => {
		const updateRequest = paymentUpdateBuilder(
			dirtyFields,
			formValues,
			form,
			FORM_NAME.payment,
			timezone,
			currency,
		);
		const response = await create.mutateAsync(updateRequest);
		if (response.data.locator) {
			navigate(`../`);
		}
	};

	const currencyCodeOptions = multiCurrencyEnabled
		? currencyCodes.map(({ value }) => value)
		: [config.tenantConfiguration.currencyCode];

	const handleReadyToPost = (event: React.ChangeEvent<HTMLInputElement>) => {
		setReadyToPost(event.target.checked);
	};

	const disableBreadcrumb =
		!!policyholderLocatorFromParams || !!policyLocatorFromParams;

	return (
		<FormProvider {...methods}>
			<Box component='form' noValidate onSubmit={handleSubmit(onSubmit)}>
				<Stack spacing={2} alignItems='flex-start'>
					{!disableBreadcrumb && <ActiveLastBreadcrumb isNew />}
					<Stack spacing={2} direction='row' alignItems='center' pb={4}>
						<Typography variant='h3'>{CREATE_PAYMENT_PAGE__TITLE}</Typography>
						<PaymentStateChip state='draft' />
					</Stack>
				</Stack>
				<Grid container spacing={2}>
					<Grid item xs={6}>
						<SelectField<PaymentFormValues>
							order={0}
							name={`${FORM_NAME.payment as 'payment'}.${
								PAYMENT_SPECIAL_FIELDS.amountCurrency
							}`}
							title='Currency'
							disabled={updatePayment.isLoading}
							control={control}
							type='select'
							values={currencyCodeOptions}
						/>
					</Grid>
					<Grid item xs={6}>
						<CurrencyField
							order={1}
							name={`${FORM_NAME.payment as 'payment'}.${
								PAYMENT_SPECIAL_FIELDS.amount
							}`}
							title='Amount'
							currency={currency}
							disabled={updatePayment.isLoading}
							control={control}
							type='select'
							values={currencyCodeOptions}
						/>
					</Grid>
					<Grid item xs={6}>
						<PolicyholderLookupField<PaymentFormValues>
							order={2}
							name={`${FORM_NAME.payment as 'payment'}.${
								PAYMENT_SPECIAL_FIELDS.policyholderLocator
							}`}
							title='Policyholder'
							disabled={
								updatePayment.isLoading ||
								!!policyholderLocatorFromParams ||
								!!policyLocatorFromParams
							}
							control={control}
							type='text'
							values={currencyCodeOptions}
						/>
					</Grid>
					<Grid item xs={6}>
						<PolicyLookupField<PaymentFormValues>
							order={3}
							name={`${FORM_NAME.payment as 'payment'}.${
								PAYMENT_SPECIAL_FIELDS.policyLocator
							}`}
							title='Policy'
							disabled={updatePayment.isLoading || !!policyLocatorFromParams}
							control={control}
							type='text'
							values={currencyCodeOptions}
						/>
					</Grid>
					<FastForm
						formName={FORM_NAME.payment}
						form={form}
						productName={policyData?.productConfig?.name}
						configVersion=''
						authData={authData}
						disabled={create.isLoading}
					/>
					<Grid item xs={12} alignSelf='flex-end'>
						<Stack direction='row' justifyContent='flex-end' sx={{ w: 1 }}>
							<FormGroup>
								<FormControlLabel
									label={CREATE_PAYMENT_PAGE__READY_TO_POST_LABEL}
									control={<Checkbox defaultChecked />}
									onChange={handleReadyToPost}
									checked={readyToPost}
								/>
							</FormGroup>
						</Stack>
					</Grid>
				</Grid>
				<Stack
					spacing={2}
					direction='row'
					alignItems='center'
					justifyContent='flex-end'
					pt={4}
				>
					<Button type='button' onClick={() => navigate(-1)}>
						{CREATE_PAYMENT_PAGE__CANCEL}
					</Button>
					<Button variant='contained' type='submit' disabled={create.isLoading}>
						{CREATE_PAYMENT_PAGE__SUBMIT}
					</Button>
				</Stack>
				<DevTool control={control} />
			</Box>
		</FormProvider>
	);
};

export default CreatePaymentPage;
